import { useEffect } from "react";
import { motion } from "framer-motion";
import { GlassWater, Coffee, Store, Truck, ChefHat, Sparkles, IceCream, Snowflake, ShoppingBag, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useQuote } from "./Header";
import { SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

const APPLICATIONS = [
  { icon: GlassWater, t: "Juice & Milkshake Bars" },
  { icon: Coffee, t: "Cafes & Cold-Coffee Chains" },
  { icon: IceCream, t: "Ice Cream & Dessert Brands" },
  { icon: ChefHat, t: "Cloud Kitchens" },
  { icon: Store, t: "Sweet Shops & Bakeries" },
  { icon: Truck, t: "Beverage Delivery" },
  { icon: Sparkles, t: "Mocktail / Bubble Tea Brands" },
  { icon: Snowflake, t: "Frozen Yogurt & Falooda Shops" },
  { icon: ShoppingBag, t: "Event Caterers & QSRs" },
];

const SIZES = [
  { label: "200 ML Sipper Glasses", short: "200 ml", tag: "Small serve", desc: "Compact sipper glass for lassi, buttermilk, mocktails and juice samplers.", uses: "Lassi • Buttermilk • Mocktails" },
  { label: "250 ML Sipper Glasses", short: "250 ml", tag: "Regular serve", desc: "Popular sipper glass size for milkshakes, cold coffee and cold beverages.", uses: "Milkshake • Cold coffee" },
  { label: "300 ML Sipper Glasses", short: "300 ml", tag: "Standard cup", desc: "Ideal cup size for smoothies, iced beverages and takeaway shakes.", uses: "Smoothie • Iced drinks" },
  { label: "400 ML Sipper Glasses", short: "400 ml", tag: "Large serve", desc: "Larger sipper glass for milkshakes, thick shakes, falooda and combo drinks.", uses: "Thick shake • Falooda" },
  { label: "500 ML Sipper Glasses", short: "500 ml", tag: "XL / bubble tea", desc: "Extra-large sipper glass for bubble tea, XL milkshakes and premium mocktails.", uses: "Bubble tea • XL shake" },
];

const TYPES = [
  "Sipper glasses with dome lids",
  "Sipper glasses with flat lids",
  "Sipper glasses with straw hole lids",
  "Crystal-clear PET sipper glasses",
  "Bubble tea / smoothie cups",
  "Custom-printed sipper glasses",
];

const FAQS = [
  { q: "What are sipper glasses used for?",
    a: "Sipper glasses (also called sipper cups) are used by juice bars, cafes, milkshake shops, ice-cream brands, cloud kitchens and dessert brands to serve milkshakes, smoothies, cold coffee, mocktails, bubble tea, lassi and other cold beverages for takeaway and delivery." },
  { q: "Do you supply sipper glasses in wholesale quantities?",
    a: "Yes. Chennai Packs supplies sipper glasses in wholesale/bulk quantities to juice bars, cafes, cloud kitchens and beverage brands. Share your required size, lid type and quantity for pricing." },
  { q: "What sizes of sipper glasses are available?",
    a: "Our sipper glass range covers 200 ml, 250 ml, 300 ml, 400 ml and 500 ml — suitable for milkshakes, smoothies, bubble tea, mocktails and other cold beverages. Enquire with your required size and quantity for availability." },
  { q: "Do sipper glasses come with lids?",
    a: "Yes. Dome lids, flat lids and straw-hole lids are available with our sipper glasses. Confirm the lid type when requesting a quote." },
  { q: "Are sipper glasses food-grade?",
    a: "Yes. Our sipper glasses are food-grade crystal-clear PET, suitable for cold beverages including milkshakes, smoothies, mocktails and cold coffee." },
  { q: "Can I get sipper glasses with my brand printed on them?",
    a: "Yes. Custom-printed sipper glasses with your logo and artwork are available at eligible MOQs. Share your logo, cup size and quantity to confirm feasibility and pricing." },
  { q: "Do you supply sipper glasses in Chennai?",
    a: "Yes. Chennai Packs supplies sipper glasses to juice bars, cafes, cloud kitchens, ice-cream brands and beverage businesses across Chennai, Tamil Nadu and pan-India." },
  { q: "How can I get a wholesale quote?",
    a: "Click ‘Get a Quote’ or ‘Enquire on WhatsApp’ on this page, share your required sipper glass size, lid type and quantity, and our team will respond with wholesale pricing." },
];

export default function SipperGlassesContent() {
  const openQuote = useQuote();
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for Sipper Glasses.")}`;

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-sipper-faq", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document.head.querySelectorAll('script[data-sipper-faq="1"]').forEach((n) => n.remove());
    };
  }, []);

  return (
    <>
      <section className="py-16" data-testid="sipper-intro">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Sipper Glasses for Milkshakes, Smoothies & Cold Beverages
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies food-grade crystal-clear sipper glasses for juice bars, cafes,
            milkshake shops, ice-cream brands, cloud kitchens and beverage brands. Available in
            multiple sizes with dome, flat and straw-hole lid options.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="sipper-sizes">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Sipper Glass Sizes
          </h2>
          <p className="text-[#3A4D3E] mt-3 max-w-2xl">
            Our sipper glasses are stocked in 200 ml to 500 ml — from small mocktail cups to XL
            bubble tea glasses. All sizes are available with matching lids on request.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SIZES.map((s) => {
              const waMsg = `Hi Chennai Packs, I'd like a quote for Sipper Glasses — ${s.short}. Please share pricing and MOQ. Thank you.`;
              const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
              return (
                <article
                  key={s.short}
                  className="bg-white border border-[#E2E8E4] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
                  data-testid={`sipper-size-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                >
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#1D6B2A]">
                    {s.tag}
                  </span>
                  <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-1">
                    {s.label}
                  </h3>
                  <p className="text-sm text-[#3A4D3E] mt-2 leading-relaxed">{s.desc}</p>
                  <p className="text-xs text-[#0A1C0E] mt-3 font-medium">{s.uses}</p>
                  <div className="mt-5 flex flex-col gap-2">
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("Sipper Size WhatsApp", { size: s.short })}
                      data-testid={`sipper-wa-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                      className="inline-flex items-center justify-center gap-2 border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#F8FAF8] transition-colors"
                    >
                      <MessageCircle size={14} /> WhatsApp for {s.short}
                    </a>
                    <button
                      onClick={() => openQuote({ name: `Sipper Glasses — ${s.short}`, sku: "CP-013" })}
                      data-testid={`sipper-quote-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                      className="bg-[#1D6B2A] text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#154d1e] transition-colors"
                    >
                      Get a Quote for {s.short}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="sipper-applications">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Ideal for Beverage & Dessert Brands
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPLICATIONS.map((a) => (
              <div key={a.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <a.icon size={22} />
                </span>
                <span className="font-head font-semibold text-[#0A1C0E]">{a.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="sipper-types">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Sipper Glass Options
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="bg-white border border-[#E2E8E4] rounded-xl px-5 py-4 text-[#0A1C0E]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="sipper-wholesale">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Wholesale Sipper Glasses in Chennai
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies sipper glasses to juice bars, cafes, cloud kitchens,
            ice-cream brands and beverage businesses in Chennai. Enquire with your required
            size, lid type and quantity for wholesale pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => openQuote({ name: "Sipper Glasses", sku: "CP-013" })}
              data-testid="sipper-wholesale-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="sipper-branding">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Custom Printed Sipper Glasses
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Print your logo and artwork on sipper glasses to build brand recall on every serve.
            Custom printing is available at eligible MOQs. Share your logo, required cup size
            and quantity — our team will confirm feasibility and pricing.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="sipper-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3 mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`sipper-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-base font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAF8]" data-testid="sipper-related">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h3 className="font-head text-xl font-semibold text-[#0A1C0E]">Related packaging you may need</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { to: "/products/juice-pet-bottles", label: "Juice PET Bottles" },
              { to: "/products/paper-cups", label: "Paper Cups" },
              { to: "/products/paper-straw", label: "Paper Straws" },
              { to: "/products/pp-biriyani-containers", label: "PP Food Containers" },
              { to: "/products?category=plastic-pp", label: "Plastic & PP Ware" },
            ].map((l) => (
              <a key={l.to} href={l.to} className="border border-[#E2E8E4] bg-white rounded-full px-5 py-2 text-sm font-medium text-[#1D6B2A] hover:bg-[#F0F4F0]">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="sipper-final-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]"
          >
            Looking for Sipper Glasses in Chennai?
          </motion.h2>
          <p className="text-lg text-[#3A4D3E] mt-4 max-w-2xl mx-auto">
            Tell us your required cup size, lid type and quantity. Enquire with Chennai Packs
            for availability and wholesale pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => openQuote({ name: "Sipper Glasses", sku: "CP-013" })}
              data-testid="sipper-final-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
